import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  noindex?: boolean;
  jsonLd?: object | object[];
  breadcrumbs?: { name: string; url: string }[];
}

const SITE_NAME = 'The Bulk Image Generator';
const DEFAULT_IMAGE = 'https://thebulkimagegenerator.com/og-image.png';
const SITE_URL = 'https://thebulkimagegenerator.com';

export const SEO = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article,
  noindex = false,
  jsonLd,
  breadcrumbs,
}: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = url ? `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}` : SITE_URL;

  // Auto-generate breadcrumb schema if breadcrumbs provided
  const breadcrumbSchema = breadcrumbs ? createBreadcrumbSchema(breadcrumbs) : null;
  
  // Combine all JSON-LD schemas
  const allSchemas = [
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags - Global Markets (20+ Countries) */}
      <link rel="alternate" hrefLang="en-US" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-GB" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-AU" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-CA" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-IN" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-DE" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-FR" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-SG" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-ES" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-IT" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-AE" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-ZA" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-NL" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bulkimagegen" />
      <meta name="twitter:creator" content="@bulkimagegen" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Article specific */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      
      {/* JSON-LD Structured Data */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// Pre-configured JSON-LD schemas
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  logo: 'https://thebulkimagegenerator.com/logo.png',
  description: 'AI-powered bulk image generation platform using FLUX models for e-commerce, marketing, and creative professionals',
  foundingDate: '2024',
  areaServed: [
    { '@type': 'Country', name: 'United States' },
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'Country', name: 'Germany' },
    { '@type': 'Country', name: 'France' },
    { '@type': 'Country', name: 'Spain' },
    { '@type': 'Country', name: 'Italy' },
    { '@type': 'Country', name: 'Netherlands' },
    { '@type': 'Country', name: 'India' },
    { '@type': 'Country', name: 'Singapore' },
    { '@type': 'Country', name: 'Japan' },
    { '@type': 'Country', name: 'Brazil' },
    { '@type': 'Country', name: 'Mexico' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'Country', name: 'South Africa' },
    { '@type': 'Country', name: 'Nigeria' },
    { '@type': 'Country', name: 'Thailand' },
    { '@type': 'Country', name: 'Malaysia' },
    { '@type': 'Country', name: 'New Zealand' },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@aigeneratedbulkimages.com',
    contactType: 'customer service',
    availableLanguage: ['English'],
  },
  sameAs: [
    'https://twitter.com/bulkimagegen',
    'https://linkedin.com/company/bulk-image-generator',
  ],
};

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'The Bulk Image Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web',
  description: 'Generate 1000+ AI images per hour with FLUX.1 Pro. Best for e-commerce, Etsy, Amazon, and marketing.',
  offers: [
    { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free tier with 5 images daily' },
    { '@type': 'Offer', price: '7.99', priceCurrency: 'USD', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'United States' } },
    { '@type': 'Offer', price: '6.99', priceCurrency: 'GBP', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'United Kingdom' } },
    { '@type': 'Offer', price: '11.99', priceCurrency: 'AUD', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'Australia' } },
    { '@type': 'Offer', price: '10.99', priceCurrency: 'CAD', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'Canada' } },
    { '@type': 'Offer', price: '7.99', priceCurrency: 'EUR', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'Germany' } },
    { '@type': 'Offer', price: '699', priceCurrency: 'INR', description: 'Hobby plan', eligibleRegion: { '@type': 'Country', name: 'India' } },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '2847',
    bestRating: '5',
    worstRating: '1',
  },
  featureList: [
    'Bulk AI image generation',
    'FLUX.1 Pro model access',
    'E-commerce product photos',
    'Marketing visuals',
    'Background removal',
    'Image enhancement',
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  description: 'AI-powered bulk image generation platform using FLUX models',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://thebulkimagegenerator.com/prompt-library?search={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }
};

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://thebulkimagegenerator.com${item.url}`
  }))
});

export const createArticleSchema = (article: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  url: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  author: {
    '@type': 'Person',
    name: article.author,
  },
  publisher: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thebulkimagegenerator.com/logo.png',
    },
  },
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  image: article.image,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `https://thebulkimagegenerator.com${article.url}`,
  },
});

export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Review Schema for rich snippets
export const reviewSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'The Bulk Image Generator',
  description: 'AI-powered bulk image generation platform',
  brand: {
    '@type': 'Brand',
    name: 'The Bulk Image Generator',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '2847',
    bestRating: '5',
    worstRating: '1',
  },
  review: [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Sarah M.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'Generated 500 product images in 30 minutes! Perfect for my Etsy store.',
      datePublished: '2026-01-10',
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'James K.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'Best AI image generator for Amazon FBA products. Saved hours of photography.',
      datePublished: '2026-01-08',
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Emily R.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'The FLUX model quality is incredible. Our marketing team uses it daily.',
      datePublished: '2026-01-05',
    },
  ],
};

// Pricing Table Schema for rich results
export const pricingTableSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'The Bulk Image Generator',
  description: 'AI bulk image generation platform with multiple pricing tiers',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: '0',
    highPrice: '99',
    offerCount: '4',
    offers: [
      {
        '@type': 'Offer',
        name: 'Free Plan',
        price: '0',
        priceCurrency: 'USD',
        description: '5 images per day, basic FLUX model',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Hobby Plan',
        price: '7.99',
        priceCurrency: 'USD',
        description: '100 images per month, all models',
        priceValidUntil: '2026-12-31',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Creator Plan',
        price: '19.99',
        priceCurrency: 'USD',
        description: '500 images per month, priority queue',
        priceValidUntil: '2026-12-31',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Professional Plan',
        price: '49.99',
        priceCurrency: 'USD',
        description: '2000 images per month, API access',
        priceValidUntil: '2026-12-31',
        availability: 'https://schema.org/InStock',
      },
    ],
  },
};

// Product/Pricing Schema for rich results
export const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'The Bulk Image Generator',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web Browser',
  offers: [
    {
      '@type': 'Offer',
      name: 'Free Plan',
      price: '0',
      priceCurrency: 'USD',
      description: '5 images per day, basic models',
    },
    {
      '@type': 'Offer',
      name: 'Pro Plan',
      price: '29',
      priceCurrency: 'USD',
      priceValidUntil: '2026-12-31',
      description: '500 images per month, all models, priority queue',
    },
    {
      '@type': 'Offer',
      name: 'Business Plan',
      price: '99',
      priceCurrency: 'USD',
      description: 'Unlimited images, API access, dedicated support',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '2847',
    bestRating: '5',
  },
};

// HowTo Schema for documentation/tutorials
export const createHowToSchema = (steps: { name: string; text: string; image?: string }[], title: string, description: string, totalTime?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: title,
  description: description,
  totalTime: totalTime || 'PT10M',
  image: 'https://thebulkimagegenerator.com/og-image.png',
  step: steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
    image: step.image,
  })),
});

// Video Schema for tutorials
export const createVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.name,
  description: video.description,
  thumbnailUrl: video.thumbnailUrl,
  uploadDate: video.uploadDate,
  duration: video.duration,
  contentUrl: video.contentUrl,
  publisher: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thebulkimagegenerator.com/logo.png',
    },
  },
});

// Software Company Schema for trust signals - US & UK Markets
export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareCompany',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  logo: 'https://thebulkimagegenerator.com/logo.png',
  description: 'AI-powered bulk image generation platform using FLUX models for e-commerce sellers, marketers, and creative professionals in the US, UK, and worldwide',
  areaServed: [
    { '@type': 'Country', name: 'United States' },
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'Country', name: 'Canada' },
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'Country', name: 'Germany' },
    { '@type': 'Country', name: 'France' },
    { '@type': 'Country', name: 'Spain' },
    { '@type': 'Country', name: 'Italy' },
    { '@type': 'Country', name: 'Netherlands' },
    { '@type': 'Country', name: 'India' },
    { '@type': 'Country', name: 'Singapore' },
    { '@type': 'Country', name: 'Japan' },
    { '@type': 'Country', name: 'Brazil' },
    { '@type': 'Country', name: 'Mexico' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'Country', name: 'South Africa' },
    { '@type': 'Country', name: 'Nigeria' },
    { '@type': 'Country', name: 'Thailand' },
    { '@type': 'Country', name: 'Malaysia' },
    { '@type': 'Country', name: 'New Zealand' },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@aigeneratedbulkimages.com',
    availableLanguage: ['English'],
  },
  sameAs: [
    'https://twitter.com/bulkimagegen',
    'https://linkedin.com/company/bulk-image-generator',
  ],
  priceRange: 'Free - $99/month',
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00',
    closes: '23:59',
  },
};

// Industry-specific schemas
export const createIndustrySchema = (industry: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: `AI Image Generation for ${industry}`,
  provider: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
  },
  description: description,
  serviceType: 'AI Image Generation',
  areaServed: 'Worldwide',
});

// ============================================
// NEW: Advanced SEO Schemas for World-Class Optimization
// ============================================

// Sitelinks SearchBox Schema - Makes Google display a search box in SERP
export const sitelinksSearchBoxSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'The Bulk Image Generator',
  url: 'https://thebulkimagegenerator.com',
  potentialAction: [{
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://thebulkimagegenerator.com/prompt-library?search={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }]
};

// Speakable Schema - Voice Search Optimization (Google Assistant, Alexa)
export const createSpeakableSchema = (url: string, selectors: string[]) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  url: `https://thebulkimagegenerator.com${url}`,
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: selectors
  }
});

// SiteNavigationElement Schema - Better sitelinks in SERP
export const navigationSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Main Navigation',
  itemListElement: [
    { '@type': 'SiteNavigationElement', position: 1, name: 'AI Image Generator', url: 'https://thebulkimagegenerator.com/try-generator' },
    { '@type': 'SiteNavigationElement', position: 2, name: 'Gallery', url: 'https://thebulkimagegenerator.com/gallery' },
    { '@type': 'SiteNavigationElement', position: 3, name: 'Pricing', url: 'https://thebulkimagegenerator.com/#pricing' },
    { '@type': 'SiteNavigationElement', position: 4, name: 'Blog', url: 'https://thebulkimagegenerator.com/blog' },
    { '@type': 'SiteNavigationElement', position: 5, name: 'Image Tools', url: 'https://thebulkimagegenerator.com/image-tools' },
    { '@type': 'SiteNavigationElement', position: 6, name: 'Prompt Library', url: 'https://thebulkimagegenerator.com/prompt-library' },
    { '@type': 'SiteNavigationElement', position: 7, name: 'Help Center', url: 'https://thebulkimagegenerator.com/help' },
  ]
};

// AI Tool / CreativeWorkSeries Schema - Optimized for AI-first search engines (2025-2026)
export const aiToolSchema = {
  '@context': 'https://schema.org',
  '@type': 'CreativeWorkSeries',
  name: 'The Bulk Image Generator AI Platform',
  description: 'Enterprise AI-powered bulk image generation using FLUX models for e-commerce, marketing, and creative professionals',
  url: 'https://thebulkimagegenerator.com',
  provider: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    url: 'https://thebulkimagegenerator.com',
  },
  genre: 'AI Image Generation',
  hasPart: [
    { '@type': 'SoftwareApplication', name: 'Bulk AI Generator', applicationCategory: 'DesignApplication', operatingSystem: 'Web' },
    { '@type': 'SoftwareApplication', name: 'Background Remover', applicationCategory: 'PhotographyApplication', operatingSystem: 'Web' },
    { '@type': 'SoftwareApplication', name: 'Image Enhancer', applicationCategory: 'PhotographyApplication', operatingSystem: 'Web' },
    { '@type': 'SoftwareApplication', name: 'Format Converter', applicationCategory: 'MultimediaApplication', operatingSystem: 'Web' },
    { '@type': 'SoftwareApplication', name: 'Embroidery Converter', applicationCategory: 'DesignApplication', operatingSystem: 'Web' },
  ],
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: '0',
    highPrice: '99',
    offerCount: '4',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '2847',
    bestRating: '5',
    worstRating: '1',
  },
};

// TechArticle Schema - For documentation and technical content
export const createTechArticleSchema = (article: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  proficiencyLevel?: 'Beginner' | 'Intermediate' | 'Expert';
}) => ({
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: article.title,
  description: article.description,
  proficiencyLevel: article.proficiencyLevel || 'Beginner',
  dependencies: 'Web browser, internet connection',
  author: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    url: 'https://thebulkimagegenerator.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thebulkimagegenerator.com/logo.png',
    },
  },
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  mainEntityOfPage: `https://thebulkimagegenerator.com${article.url}`,
});

// Course Schema - For video tutorials
export const courseSchema = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'Master AI Image Generation',
  description: 'Complete video course on using FLUX AI for professional image creation. Learn prompt engineering, bulk generation, and e-commerce workflows.',
  provider: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    url: 'https://thebulkimagegenerator.com',
  },
  isAccessibleForFree: true,
  hasCourseInstance: {
    '@type': 'CourseInstance',
    courseMode: 'Online',
    courseWorkload: 'PT2H',
    instructor: {
      '@type': 'Person',
      name: 'Abhishek Zinzuvadiya',
      jobTitle: 'Founder & CEO',
    },
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
  },
  educationalLevel: 'Beginner to Advanced',
  teaches: ['AI Image Generation', 'Prompt Engineering', 'Bulk Generation', 'E-commerce Photography'],
};

// ImageGallery Schema - For gallery pages
export const createImageGallerySchema = (images: { url: string; name: string; description: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: 'AI Generated Image Gallery',
  description: 'Browse thousands of AI-generated images created with FLUX models. Get prompt inspiration for product photos, marketing visuals, and creative art.',
  url: 'https://thebulkimagegenerator.com/gallery',
  image: images.map((img, index) => ({
    '@type': 'ImageObject',
    position: index + 1,
    contentUrl: img.url,
    name: img.name,
    description: img.description,
    creator: {
      '@type': 'Organization',
      name: 'The Bulk Image Generator',
    },
  })),
});

// Enhanced Author/Person Schema - E-E-A-T signals
export const authorSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Abhishek Zinzuvadiya',
  jobTitle: 'Founder & CEO',
  description: 'Software engineer and AI entrepreneur with expertise in image generation technology, machine learning, and SaaS development.',
  url: 'https://thebulkimagegenerator.com/about',
  knowsAbout: [
    'AI Image Generation',
    'FLUX Models',
    'Machine Learning',
    'E-commerce Photography',
    'SaaS Development',
    'Product Photography',
    'Marketing Technology',
  ],
  hasCredential: [
    { '@type': 'EducationalOccupationalCredential', name: 'Electronics and Communication Engineering' }
  ],
  worksFor: {
    '@type': 'Organization',
    name: 'The Bulk Image Generator',
    url: 'https://thebulkimagegenerator.com',
  },
  sameAs: [
    'https://linkedin.com/in/abhishekzinzuvadiya',
    'https://twitter.com/abhishekz',
  ],
};

// ProfilePage Schema - For about/author pages
export const profilePageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: authorSchema,
  dateCreated: '2024-01-01',
  dateModified: '2026-01-31',
};

// Enhanced homepage schema array for maximum rich results
export const homepageSchemas = [
  organizationSchema,
  websiteSchema,
  sitelinksSearchBoxSchema,
  navigationSchema,
  aiToolSchema,
  softwareApplicationSchema,
  reviewSchema,
  pricingTableSchema,
];

export default SEO;
